import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    Modal,
    Steps,
    Select,
    Input,
    Button,
    Table,
    Row,
    Col,
    Typography,
    Tag,
    Space,
    Alert,
    message,
    Switch,
    Upload,
    Radio,
} from 'antd';
import {
    ArrowRightOutlined,
    ArrowLeftOutlined,
    CheckOutlined,
    EditOutlined,
    CopyOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { readFirstSheetAsObjects } from '../../utils/workbook';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetTemplatesByTypeQuery,
    useCloneTemplateTypeMutation,
    useImportTemplateTypeMutation,
    IQuestionTemplate,
} from '../../services/nestjs-base-api';
import { FormattedMessage, useIntl } from 'react-intl';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;


const TEMPLATE_TYPE_PATTERN = /^[a-z][a-z0-9_]*$/;

const QUESTION_TYPE_LABELS: Record<number, string> = {
    1: 'Likert 7',
    2: 'Likert 5',
    3: 'Yes/No',
    4: 'Rating 10',
    5: 'Text',
    6: 'Multiple Choice',
    7: 'NPS',
};

const SCORING_TYPE_OPTIONS = [
    'likert_7_standard',
    'likert_7_zero_based',
    'likert_7_binary',
    'likert_7_flipped',
    'likert_7_with_na_standard',
    'likert_5_standard',
    'likert_5_zero_based',
    'likert_5_binary',
    'nps_standard',
    'nps_categories',
    'rating_10_standard',
    'rating_10_normalized',
    'yes_no_standard',
    'yes_no_inverted',
    'custom',
];

interface TemplateModification {
    code: string;
    text?: string;
    scoringType?: string;
    status?: 'active' | 'inactive';
}

const SCORING_TYPE_SET = new Set(SCORING_TYPE_OPTIONS);

// Spreadsheet rows arrive with whatever header capitalisation Excel/Claude
// produced — normalise keys to lowercase so lookups are stable.
const normalizeRow = (row: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {};
    Object.keys(row).forEach((key) => {
        out[key.trim().toLowerCase()] = String(row[key] ?? '').trim();
    });
    return out;
};

const parseBool = (value: string): boolean | null => {
    const v = value.toLowerCase();
    if (['yes', 'y', 'true', '1'].includes(v)) return true;
    if (['no', 'n', 'false', '0'].includes(v)) return false;
    return null;
};

// Inverse of the export's Question Type labels ("Likert 7" -> 1). Kept
// round-trip consistent with the Download CSV button rather than DB names.
const QUESTION_TYPE_ID_BY_LABEL: Record<string, number> = Object.fromEntries(
    Object.entries(QUESTION_TYPE_LABELS).map(([id, label]) => [
        label.toLowerCase(),
        Number(id),
    ]),
);

interface CloneTemplateTypeWizardProps {
    open: boolean;
    onClose: () => void;
    templateTypes: string[];
}

const formatTemplateTypeName = (type: string): string => {
    return type
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const CloneTemplateTypeWizard: React.FC<
    CloneTemplateTypeWizardProps
> = ({ open, onClose, templateTypes }) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [currentStep, setCurrentStep] = useState(0);
    const [mode, setMode] = useState<'clone' | 'upload'>('clone');
    const [sourceType, setSourceType] = useState<string | undefined>(
        undefined,
    );
    const [newTypeName, setNewTypeName] = useState('');
    const [modifications, setModifications] = useState<
        Map<string, TemplateModification>
    >(new Map());
    const [importedRows, setImportedRows] = useState<IQuestionTemplate[]>([]);

    const { data: sourceData, isLoading: sourceLoading } =
        useGetTemplatesByTypeQuery(
            {
                jwtToken: user?.jwtToken || '',
                templateType: sourceType || '',
            },
            { skip: !sourceType },
        );

    const [cloneTemplateType, { isLoading: isCloning }] =
        useCloneTemplateTypeMutation();
    const [importTemplateType, { isLoading: isImporting }] =
        useImportTemplateTypeMutation();

    const sourceTemplates = sourceData?.data || [];

    const isValidName =
        newTypeName.length > 0 &&
        TEMPLATE_TYPE_PATTERN.test(newTypeName) &&
        !templateTypes.includes(newTypeName);

    const nameError = useMemo(() => {
        if (!newTypeName) return undefined;
        if (!TEMPLATE_TYPE_PATTERN.test(newTypeName)) {
            return intl.formatMessage({ id: 'admin.templates.clone.invalidName', defaultMessage: 'Must be lower_snake_case (letters, numbers, underscores)' });
        }
        if (templateTypes.includes(newTypeName)) {
            return intl.formatMessage({ id: 'admin.templates.clone.nameExists', defaultMessage: 'This template type already exists' });
        }
        return undefined;
    }, [newTypeName, templateTypes, intl]);

    const modificationCount = useMemo(() => {
        let count = 0;
        modifications.forEach((mod) => {
            if (mod.text || mod.scoringType || mod.status) count++;
        });
        return count;
    }, [modifications]);

    const updateModification = useCallback(
        (code: string, field: keyof TemplateModification, value: string | undefined) => {
            setModifications((prev) => {
                const next = new Map(prev);
                const existing = next.get(code) || { code };
                if (value === undefined) {
                    delete (existing as any)[field];
                } else {
                    (existing as any)[field] = value;
                }
                const hasChanges = existing.text || existing.scoringType || existing.status;
                if (!hasChanges) {
                    next.delete(code);
                } else {
                    next.set(code, existing);
                }
                return next;
            });
        },
        [],
    );

    const handleModifyText = useCallback(
        (code: string, text: string, originalText: string) => {
            updateModification(code, 'text', text === originalText ? undefined : text);
        },
        [updateModification],
    );

    const handleModifyScoringType = useCallback(
        (code: string, scoringType: string, originalScoringType: string) => {
            updateModification(code, 'scoringType', scoringType === originalScoringType ? undefined : scoringType);
        },
        [updateModification],
    );

    const handleModifyStatus = useCallback(
        (code: string, status: string, originalStatus: string) => {
            updateModification(code, 'status', status === originalStatus ? undefined : status);
        },
        [updateModification],
    );

    // Strict clone-shaped import: rows are matched to the source template by
    // Code, only text / scoring type / status may differ. Any structural drift
    // (category, question type, core, required, order) is a hard error so a
    // spreadsheet can never invent an element the scoring model doesn't know.
    const handleImportFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            let rows: Record<string, string>[];
            try {
                rows = (
                    await readFirstSheetAsObjects(
                        e.target?.result as ArrayBuffer,
                    )
                ).map(normalizeRow);
            } catch {
                message.error(
                    intl.formatMessage({
                        id: 'admin.templates.import.readFailed',
                        defaultMessage:
                            'Could not read the file — expected a .csv or .xlsx spreadsheet',
                    }),
                );
                return;
            }

            const errors: string[] = [];
            const headers = new Set(
                rows.length ? Object.keys(rows[0]) : [],
            );
            const textKey = headers.has('statement text')
                ? 'statement text'
                : headers.has('text')
                  ? 'text'
                  : undefined;
            if (rows.length === 0) {
                errors.push('The file has no data rows');
            } else {
                if (!headers.has('code')) errors.push('Missing "Code" column');
                if (!textKey) errors.push('Missing "Statement Text" column');
            }

            const sourceByCode = new Map(
                sourceTemplates.map((t) => [t.code.toLowerCase(), t]),
            );
            const seen = new Set<string>();
            const next = new Map<string, TemplateModification>();

            if (errors.length === 0) {
                rows.forEach((row, idx) => {
                    const rowNum = idx + 2; // 1-based, after the header row
                    const code = row['code'];
                    if (!code) {
                        errors.push(`Row ${rowNum}: empty code`);
                        return;
                    }
                    const source = sourceByCode.get(code.toLowerCase());
                    if (!source) {
                        errors.push(
                            `Row ${rowNum}: code "${code}" does not exist in the source template type`,
                        );
                        return;
                    }
                    if (seen.has(source.code)) {
                        errors.push(`Row ${rowNum}: duplicate code "${code}"`);
                        return;
                    }
                    seen.add(source.code);

                    if (
                        headers.has('category') &&
                        row['category'].toLowerCase() !==
                            source.category.toLowerCase()
                    ) {
                        errors.push(
                            `Row ${rowNum} (${code}): category "${row['category']}" differs from source "${source.category}" — structure is locked`,
                        );
                    }
                    if (headers.has('question type')) {
                        const expected =
                            QUESTION_TYPE_LABELS[source.questionTypeId] ||
                            `Type ${source.questionTypeId}`;
                        const got = row['question type'].toLowerCase();
                        if (
                            got !== expected.toLowerCase() &&
                            got !== String(source.questionTypeId)
                        ) {
                            errors.push(
                                `Row ${rowNum} (${code}): question type "${row['question type']}" differs from source "${expected}" — structure is locked`,
                            );
                        }
                    }
                    if (headers.has('core')) {
                        const val = parseBool(row['core']);
                        if (val === null || val !== source.isCore) {
                            errors.push(
                                `Row ${rowNum} (${code}): core flag "${row['core']}" differs from source — structure is locked`,
                            );
                        }
                    }
                    if (headers.has('required')) {
                        const val = parseBool(row['required']);
                        if (val === null || val !== source.isRequired) {
                            errors.push(
                                `Row ${rowNum} (${code}): required flag "${row['required']}" differs from source — structure is locked`,
                            );
                        }
                    }
                    if (
                        headers.has('order') &&
                        Number(row['order']) !== source.displayOrder
                    ) {
                        errors.push(
                            `Row ${rowNum} (${code}): order "${row['order']}" differs from source ${source.displayOrder} — structure is locked`,
                        );
                    }

                    const text = textKey ? row[textKey] : '';
                    if (!text) {
                        errors.push(
                            `Row ${rowNum} (${code}): statement text is empty`,
                        );
                        return;
                    }

                    let scoringType: string | undefined;
                    if (headers.has('scoring type')) {
                        scoringType = row['scoring type']
                            .toLowerCase()
                            .replace(/ /g, '_');
                        if (!SCORING_TYPE_SET.has(scoringType)) {
                            errors.push(
                                `Row ${rowNum} (${code}): unknown scoring type "${row['scoring type']}"`,
                            );
                            return;
                        }
                    }
                    let status: 'active' | 'inactive' | undefined;
                    if (headers.has('status')) {
                        const s = row['status'].toLowerCase();
                        if (s !== 'active' && s !== 'inactive') {
                            errors.push(
                                `Row ${rowNum} (${code}): status must be "active" or "inactive", got "${row['status']}"`,
                            );
                            return;
                        }
                        status = s;
                    }

                    const mod: TemplateModification = { code: source.code };
                    if (text !== source.text) mod.text = text;
                    if (scoringType && scoringType !== source.scoringType) {
                        mod.scoringType = scoringType;
                    }
                    if (status && status !== source.status) {
                        mod.status = status;
                    }
                    if (mod.text || mod.scoringType || mod.status) {
                        next.set(source.code, mod);
                    }
                });
            }

            if (errors.length > 0) {
                Modal.error({
                    title: intl.formatMessage({
                        id: 'admin.templates.import.errorsTitle',
                        defaultMessage:
                            'Spreadsheet has problems — nothing was imported',
                    }),
                    width: 680,
                    content: (
                        <ul
                            style={{
                                maxHeight: 320,
                                overflowY: 'auto',
                                paddingLeft: 20,
                                marginTop: 12,
                            }}
                        >
                            {errors.slice(0, 30).map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                            {errors.length > 30 && (
                                <li>…and {errors.length - 30} more</li>
                            )}
                        </ul>
                    ),
                });
                return;
            }

            setModifications(next);
            message.success(
                intl.formatMessage(
                    {
                        id: 'admin.templates.import.success',
                        defaultMessage:
                            'Imported {rows} rows — {mods, plural, one {# statement} other {# statements}} modified',
                    },
                    { rows: rows.length, mods: next.size },
                ),
            );

            const missing = sourceTemplates.filter((t) => !seen.has(t.code));
            if (missing.length > 0) {
                Modal.warning({
                    title: intl.formatMessage({
                        id: 'admin.templates.import.missingTitle',
                        defaultMessage: 'Some statements were not in the file',
                    }),
                    width: 560,
                    content: intl.formatMessage(
                        {
                            id: 'admin.templates.import.missingContent',
                            defaultMessage:
                                '{count, plural, one {# source statement was} other {# source statements were}} not in the spreadsheet and will be cloned unchanged: {codes}',
                        },
                        {
                            count: missing.length,
                            codes:
                                missing
                                    .slice(0, 10)
                                    .map((t) => t.code)
                                    .join(', ') +
                                (missing.length > 10 ? '…' : ''),
                        },
                    ),
                });
            }
        };
        reader.readAsArrayBuffer(file);
    };

    // Upload mode: the CSV IS the template — no source type. Structure comes
    // from the file; the API rejects categories that aren't existing
    // brand-equity elements.
    const handleStandaloneFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            let rows: Record<string, string>[];
            try {
                rows = (
                    await readFirstSheetAsObjects(
                        e.target?.result as ArrayBuffer,
                    )
                ).map(normalizeRow);
            } catch {
                message.error(
                    intl.formatMessage({
                        id: 'admin.templates.import.readFailed',
                        defaultMessage:
                            'Could not read the file — expected a .csv or .xlsx spreadsheet',
                    }),
                );
                return;
            }

            const errors: string[] = [];
            const headers = new Set(
                rows.length ? Object.keys(rows[0]) : [],
            );
            const textKey = headers.has('statement text')
                ? 'statement text'
                : headers.has('text')
                  ? 'text'
                  : undefined;
            if (rows.length === 0) {
                errors.push('The file has no data rows');
            } else {
                ['code', 'category', 'question type', 'scoring type'].forEach(
                    (col) => {
                        if (!headers.has(col)) {
                            errors.push(`Missing "${col}" column`);
                        }
                    },
                );
                if (!textKey) errors.push('Missing "Statement Text" column');
            }

            const seen = new Set<string>();
            const parsed: IQuestionTemplate[] = [];

            if (errors.length === 0) {
                rows.forEach((row, idx) => {
                    const rowNum = idx + 2;
                    const code = row['code'];
                    if (!code) {
                        errors.push(`Row ${rowNum}: empty code`);
                        return;
                    }
                    if (seen.has(code.toLowerCase())) {
                        errors.push(`Row ${rowNum}: duplicate code "${code}"`);
                        return;
                    }
                    seen.add(code.toLowerCase());

                    const text = row[textKey!];
                    if (!text) {
                        errors.push(
                            `Row ${rowNum} (${code}): statement text is empty`,
                        );
                        return;
                    }
                    const category = row['category'];
                    if (!category) {
                        errors.push(
                            `Row ${rowNum} (${code}): category is empty`,
                        );
                        return;
                    }

                    const qtRaw = row['question type'].toLowerCase();
                    let questionTypeId = QUESTION_TYPE_ID_BY_LABEL[qtRaw];
                    if (!questionTypeId) {
                        const m = qtRaw.match(/^type (\d+)$/);
                        if (m) questionTypeId = Number(m[1]);
                    }
                    if (!questionTypeId) {
                        errors.push(
                            `Row ${rowNum} (${code}): unknown question type "${row['question type']}"`,
                        );
                        return;
                    }

                    const scoringType = row['scoring type']
                        .toLowerCase()
                        .replace(/ /g, '_');
                    if (!SCORING_TYPE_SET.has(scoringType)) {
                        errors.push(
                            `Row ${rowNum} (${code}): unknown scoring type "${row['scoring type']}"`,
                        );
                        return;
                    }

                    let status: 'active' | 'inactive' = 'active';
                    if (headers.has('status') && row['status']) {
                        const s = row['status'].toLowerCase();
                        if (s !== 'active' && s !== 'inactive') {
                            errors.push(
                                `Row ${rowNum} (${code}): status must be "active" or "inactive", got "${row['status']}"`,
                            );
                            return;
                        }
                        status = s;
                    }

                    const isCore = headers.has('core')
                        ? parseBool(row['core'])
                        : false;
                    const isRequired = headers.has('required')
                        ? parseBool(row['required'])
                        : true;
                    if (isCore === null || isRequired === null) {
                        errors.push(
                            `Row ${rowNum} (${code}): Core/Required must be Yes or No`,
                        );
                        return;
                    }

                    let displayOrder = idx + 1;
                    if (headers.has('order') && row['order']) {
                        displayOrder = Number(row['order']);
                        if (!Number.isFinite(displayOrder)) {
                            errors.push(
                                `Row ${rowNum} (${code}): order "${row['order']}" is not a number`,
                            );
                            return;
                        }
                    }

                    parsed.push({
                        id: idx + 1,
                        code,
                        text,
                        category,
                        questionTypeId,
                        scoringType,
                        isCore,
                        isRequired,
                        status,
                        displayOrder,
                    } as IQuestionTemplate);
                });
            }

            if (errors.length > 0) {
                setImportedRows([]);
                Modal.error({
                    title: intl.formatMessage({
                        id: 'admin.templates.import.errorsTitle',
                        defaultMessage:
                            'Spreadsheet has problems — nothing was imported',
                    }),
                    width: 680,
                    content: (
                        <ul
                            style={{
                                maxHeight: 320,
                                overflowY: 'auto',
                                paddingLeft: 20,
                                marginTop: 12,
                            }}
                        >
                            {errors.slice(0, 30).map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                            {errors.length > 30 && (
                                <li>…and {errors.length - 30} more</li>
                            )}
                        </ul>
                    ),
                });
                return;
            }

            parsed.sort((a, b) => a.displayOrder - b.displayOrder);
            setImportedRows(parsed);
            message.success(
                intl.formatMessage(
                    {
                        id: 'admin.templates.upload.loaded',
                        defaultMessage:
                            '{count, plural, one {# statement} other {# statements}} loaded from file',
                    },
                    { count: parsed.length },
                ),
            );
        };
        reader.readAsArrayBuffer(file);
    };

    const handleCreateFromUpload = async () => {
        if (!isValidName || importedRows.length === 0) return;
        try {
            await importTemplateType({
                jwtToken: user?.jwtToken || '',
                payload: {
                    newTemplateType: newTypeName,
                    templates: importedRows.map((r) => ({
                        code: r.code,
                        text: r.text,
                        category: r.category,
                        questionTypeId: r.questionTypeId,
                        scoringType: r.scoringType,
                        isCore: r.isCore,
                        isRequired: r.isRequired,
                        status: r.status as 'active' | 'inactive',
                        displayOrder: r.displayOrder,
                    })),
                },
            }).unwrap();
            message.success(
                intl.formatMessage(
                    { id: 'admin.templates.clone.success', defaultMessage: 'Template type "{type}" created successfully' },
                    { type: formatTemplateTypeName(newTypeName) },
                ),
            );
            handleClose();
        } catch (error: any) {
            message.error(
                error?.data?.message ||
                    intl.formatMessage({ id: 'admin.templates.clone.failed', defaultMessage: 'Failed to create template type' }),
            );
        }
    };

    const handleClose = () => {
        setCurrentStep(0);
        setMode('clone');
        setSourceType(undefined);
        setNewTypeName('');
        setModifications(new Map());
        setImportedRows([]);
        onClose();
    };

    const handleClone = async () => {
        if (!sourceType || !isValidName) return;

        const modificationsArray: TemplateModification[] = [];
        modifications.forEach((mod) => {
            if (mod.text || mod.scoringType || mod.status) {
                modificationsArray.push(mod);
            }
        });

        try {
            await cloneTemplateType({
                jwtToken: user?.jwtToken || '',
                payload: {
                    sourceTemplateType: sourceType,
                    newTemplateType: newTypeName,
                    modifications: modificationsArray,
                },
            }).unwrap();
            message.success(
                intl.formatMessage(
                    { id: 'admin.templates.clone.success', defaultMessage: 'Template type "{type}" created successfully' },
                    { type: formatTemplateTypeName(newTypeName) },
                ),
            );
            handleClose();
        } catch (error: any) {
            message.error(
                error?.data?.message ||
                    intl.formatMessage({ id: 'admin.templates.clone.failed', defaultMessage: 'Failed to create template type' }),
            );
        }
    };

    const sourceColumns: ColumnsType<IQuestionTemplate> = [
        {
            title: intl.formatMessage({ id: 'admin.templates.code', defaultMessage: 'Code' }),
            dataIndex: 'code',
            key: 'code',
            width: 80,
            render: (code: string) => (
                <Text code style={{ fontSize: 11 }}>
                    {code}
                </Text>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.text', defaultMessage: 'Statement Text' }),
            dataIndex: 'text',
            key: 'text',
            ellipsis: true,
            render: (text: string) => (
                <Text style={{ fontSize: 13 }}>{text}</Text>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.questionType', defaultMessage: 'Type' }),
            dataIndex: 'questionTypeId',
            key: 'questionTypeId',
            width: 90,
            render: (id: number) => (
                <Tag style={{ fontSize: 11 }}>
                    {QUESTION_TYPE_LABELS[id] || `Type ${id}`}
                </Tag>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.scoringType', defaultMessage: 'Scoring' }),
            dataIndex: 'scoringType',
            key: 'scoringType',
            width: 140,
            render: (st: string) => (
                <Tag style={{ fontSize: 11 }}>{st?.replace(/_/g, ' ')}</Tag>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.status', defaultMessage: 'Status' }),
            dataIndex: 'status',
            key: 'status',
            width: 80,
            align: 'center' as const,
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'default'} style={{ fontSize: 11 }}>
                    {status}
                </Tag>
            ),
        },
    ];

    const newColumns: ColumnsType<IQuestionTemplate> = [
        {
            title: intl.formatMessage({ id: 'admin.templates.code', defaultMessage: 'Code' }),
            dataIndex: 'code',
            key: 'code',
            width: 80,
            render: (code: string) => (
                <Text code style={{ fontSize: 11 }}>
                    {code}
                </Text>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.text', defaultMessage: 'Statement Text' }),
            dataIndex: 'text',
            key: 'text',
            render: (text: string, record: IQuestionTemplate) => {
                const mod = modifications.get(record.code);
                const currentText = mod?.text ?? text;
                const isModified = mod?.text !== undefined;
                return (
                    <TextArea
                        value={currentText}
                        onChange={(e) =>
                            handleModifyText(record.code, e.target.value, text)
                        }
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        style={{
                            fontSize: 13,
                            backgroundColor: isModified
                                ? '#f6ffed'
                                : undefined,
                            borderColor: isModified ? '#b7eb8f' : undefined,
                        }}
                    />
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.questionType', defaultMessage: 'Type' }),
            dataIndex: 'questionTypeId',
            key: 'questionTypeId',
            width: 90,
            render: (id: number) => (
                <Tag style={{ fontSize: 11 }}>
                    {QUESTION_TYPE_LABELS[id] || `Type ${id}`}
                </Tag>
            ),
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.scoringType', defaultMessage: 'Scoring' }),
            dataIndex: 'scoringType',
            key: 'scoringType',
            width: 180,
            render: (scoringType: string, record: IQuestionTemplate) => {
                const mod = modifications.get(record.code);
                const currentScoringType = mod?.scoringType ?? scoringType;
                const isModified = mod?.scoringType !== undefined;
                return (
                    <Select
                        value={currentScoringType}
                        onChange={(value) =>
                            handleModifyScoringType(
                                record.code,
                                value,
                                scoringType,
                            )
                        }
                        style={{
                            width: '100%',
                            fontSize: 11,
                        }}
                        className={isModified ? 'modified-select' : undefined}
                        size="small"
                    >
                        {SCORING_TYPE_OPTIONS.map((st) => (
                            <Select.Option key={st} value={st}>
                                {st.replace(/_/g, ' ')}
                            </Select.Option>
                        ))}
                    </Select>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'admin.templates.status', defaultMessage: 'Status' }),
            dataIndex: 'status',
            key: 'status',
            width: 80,
            align: 'center' as const,
            render: (status: string, record: IQuestionTemplate) => {
                const mod = modifications.get(record.code);
                const currentStatus = mod?.status ?? status;
                const isActive = currentStatus === 'active';
                return (
                    <Switch
                        size="small"
                        checked={isActive}
                        onChange={(checked) =>
                            handleModifyStatus(
                                record.code,
                                checked ? 'active' : 'inactive',
                                status,
                            )
                        }
                    />
                );
            },
        },
    ];

    const sourceTableRef = useRef<HTMLDivElement>(null);
    const newTableRef = useRef<HTMLDivElement>(null);
    const isSyncing = useRef(false);

    useEffect(() => {
        const sourceEl = sourceTableRef.current?.querySelector('.ant-table-body');
        const newEl = newTableRef.current?.querySelector('.ant-table-body');
        if (!sourceEl || !newEl) return;

        const syncScroll = (source: Element, target: Element) => () => {
            if (isSyncing.current) return;
            isSyncing.current = true;
            target.scrollTop = source.scrollTop;
            isSyncing.current = false;
        };

        const handleSourceScroll = syncScroll(sourceEl, newEl);
        const handleNewScroll = syncScroll(newEl, sourceEl);

        sourceEl.addEventListener('scroll', handleSourceScroll);
        newEl.addEventListener('scroll', handleNewScroll);

        return () => {
            sourceEl.removeEventListener('scroll', handleSourceScroll);
            newEl.removeEventListener('scroll', handleNewScroll);
        };
    }, [currentStep, sourceTemplates]);

    const canProceedStep0 =
        isValidName &&
        (mode === 'clone' ? !!sourceType : importedRows.length > 0);
    const canProceedStep1 = true;

    const renderStep0 = () => (
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Radio.Group
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    style={{ display: 'flex' }}
                    options={[
                        {
                            value: 'clone',
                            label: intl.formatMessage({
                                id: 'admin.templates.mode.clone',
                                defaultMessage: 'Clone existing type',
                            }),
                            style: { flex: 1, textAlign: 'center' },
                        },
                        {
                            value: 'upload',
                            label: intl.formatMessage({
                                id: 'admin.templates.mode.upload',
                                defaultMessage: 'Upload spreadsheet',
                            }),
                            style: { flex: 1, textAlign: 'center' },
                        },
                    ]}
                />

                {mode === 'clone' ? (
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            <FormattedMessage
                                id="admin.templates.clone.sourceLabel"
                                defaultMessage="Source Template Type"
                            />
                        </Text>
                        <Select
                            placeholder={intl.formatMessage({ id: 'admin.templates.clone.selectSource', defaultMessage: 'Select template to clone from...' })}
                            value={sourceType}
                            onChange={setSourceType}
                            style={{ width: '100%' }}
                            size="large"
                        >
                            {templateTypes.map((type) => (
                                <Select.Option key={type} value={type}>
                                    {formatTemplateTypeName(type)}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                ) : (
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            <FormattedMessage
                                id="admin.templates.upload.fileLabel"
                                defaultMessage="Statement Spreadsheet"
                            />
                        </Text>
                        <Upload
                            accept=".csv,.xlsx"
                            showUploadList={false}
                            className="upload-full-width"
                            beforeUpload={(file) => {
                                handleStandaloneFile(file);
                                return false;
                            }}
                        >
                            <Button
                                icon={<UploadOutlined />}
                                size="large"
                                block
                            >
                                {importedRows.length > 0
                                    ? intl.formatMessage(
                                          {
                                              id: 'admin.templates.upload.replaceFile',
                                              defaultMessage:
                                                  '{count} statements loaded — click to replace',
                                          },
                                          { count: importedRows.length },
                                      )
                                    : intl.formatMessage({
                                          id: 'admin.templates.upload.chooseFile',
                                          defaultMessage:
                                              'Upload .csv or .xlsx file',
                                      })}
                            </Button>
                        </Upload>
                        <Paragraph
                            type="secondary"
                            style={{ fontSize: 12, marginTop: 4 }}
                        >
                            <FormattedMessage
                                id="admin.templates.upload.hint"
                                defaultMessage="Use the format from Download CSV: Code, Statement Text, Category, Question Type, Scoring Type, Core, Required, Status, Order. Categories must be existing brand-equity elements."
                            />
                        </Paragraph>
                    </div>
                )}

                <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        <FormattedMessage
                            id="admin.templates.clone.newNameLabel"
                            defaultMessage="New Template Type Name"
                        />
                    </Text>
                    <Input
                        placeholder="e.g. luxury_fashion, tech_saas, financial_services"
                        value={newTypeName}
                        onChange={(e) =>
                            setNewTypeName(
                                e.target.value.toLowerCase().replace(/\s/g, '_'),
                            )
                        }
                        size="large"
                        status={nameError ? 'error' : undefined}
                    />
                    {nameError && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                            {nameError}
                        </Text>
                    )}
                    {!nameError && newTypeName && (
                        <Text type="success" style={{ fontSize: 12 }}>
                            <FormattedMessage
                                id="admin.templates.clone.namePreview"
                                defaultMessage='Will display as: "{name}"'
                                values={{
                                    name: formatTemplateTypeName(newTypeName),
                                }}
                            />
                        </Text>
                    )}
                    <Paragraph
                        type="secondary"
                        style={{ fontSize: 12, marginTop: 4 }}
                    >
                        <FormattedMessage
                            id="admin.templates.clone.nameGuidance"
                            defaultMessage="Use lower_snake_case. Describe the product category or industry vertical this template set is designed for."
                        />
                    </Paragraph>
                </div>
            </Space>
        </div>
    );

    const uploadPreviewColumns: ColumnsType<IQuestionTemplate> = [
        ...sourceColumns.slice(0, 2),
        {
            title: intl.formatMessage({ id: 'admin.templates.category', defaultMessage: 'Category' }),
            dataIndex: 'category',
            key: 'category',
            width: 170,
            render: (category: string) => (
                <Tag style={{ fontSize: 11 }}>{category}</Tag>
            ),
        },
        ...sourceColumns.slice(2),
    ];

    const renderUploadPreview = () => (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<EditOutlined />}
                message={
                    <FormattedMessage
                        id="admin.templates.upload.previewHint"
                        defaultMessage='Review the statements parsed from the file. To change anything, edit the spreadsheet and re-upload it in the previous step.'
                    />
                }
                style={{ marginBottom: 16 }}
            />
            <Table
                columns={uploadPreviewColumns}
                dataSource={importedRows}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ y: 500 }}
                rowClassName={(record) =>
                    record.status === 'inactive' ? 'wizard-inactive-row' : ''
                }
            />
        </div>
    );

    const renderStep1 = () => (
        <div>
            <Row gutter={12} align="middle" wrap={false} style={{ marginBottom: 16 }}>
                <Col flex="auto">
                    <Alert
                        type="info"
                        showIcon
                        icon={<EditOutlined />}
                        message={
                            <FormattedMessage
                                id="admin.templates.clone.editHint"
                                defaultMessage="Edit the statement text and scoring type on the right to customise the new template, or import a spreadsheet (CSV/Excel) exported from an existing template. {count} modifications so far."
                                values={{ count: modificationCount }}
                            />
                        }
                    />
                </Col>
                <Col>
                    <Upload
                        accept=".csv,.xlsx"
                        showUploadList={false}
                        beforeUpload={(file) => {
                            handleImportFile(file);
                            return false;
                        }}
                    >
                        <Button icon={<UploadOutlined />}>
                            <FormattedMessage
                                id="admin.templates.import.button"
                                defaultMessage="Import Spreadsheet"
                            />
                        </Button>
                    </Upload>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Title level={5} style={{ marginBottom: 8 }}>
                        <FormattedMessage
                            id="admin.templates.clone.sourceTitle"
                            defaultMessage="Source: {type}"
                            values={{
                                type: formatTemplateTypeName(sourceType || ''),
                            }}
                        />
                    </Title>
                    <div ref={sourceTableRef}>
                        <Table
                            columns={sourceColumns}
                            dataSource={sourceTemplates}
                            loading={sourceLoading}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            scroll={{ y: 500 }}
                            rowClassName={(record) =>
                                record.status === 'inactive'
                                    ? 'wizard-inactive-row'
                                    : ''
                            }
                        />
                    </div>
                </Col>
                <Col span={12}>
                    <Title level={5} style={{ marginBottom: 8 }}>
                        <FormattedMessage
                            id="admin.templates.clone.newTitle"
                            defaultMessage="New: {type}"
                            values={{
                                type: formatTemplateTypeName(newTypeName),
                            }}
                        />
                    </Title>
                    <div ref={newTableRef}>
                        <Table
                            columns={newColumns}
                            dataSource={sourceTemplates}
                            loading={sourceLoading}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            scroll={{ y: 500 }}
                            rowClassName={(record) => {
                                const mod = modifications.get(record.code);
                                const currentStatus = mod?.status ?? record.status;
                                return currentStatus === 'inactive'
                                    ? 'wizard-inactive-row'
                                    : '';
                            }}
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );

    const renderStep2 = () => (
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                    type="success"
                    showIcon
                    message={
                        <FormattedMessage
                            id="admin.templates.clone.readyMessage"
                            defaultMessage="Ready to create template type"
                        />
                    }
                />
                <div>
                    <Text type="secondary">
                        <FormattedMessage
                            id="admin.templates.clone.summarySource"
                            defaultMessage="Source:"
                        />
                    </Text>{' '}
                    <Tag color="blue">
                        {mode === 'clone'
                            ? formatTemplateTypeName(sourceType || '')
                            : intl.formatMessage({
                                  id: 'admin.templates.upload.sourceFile',
                                  defaultMessage: 'Uploaded spreadsheet',
                              })}
                    </Tag>
                </div>
                <div>
                    <Text type="secondary">
                        <FormattedMessage
                            id="admin.templates.clone.summaryNew"
                            defaultMessage="New type:"
                        />
                    </Text>{' '}
                    <Tag color="green">
                        {formatTemplateTypeName(newTypeName)}
                    </Tag>
                    <Text code style={{ marginLeft: 8, fontSize: 11 }}>
                        {newTypeName}
                    </Text>
                </div>
                <div>
                    <Text type="secondary">
                        <FormattedMessage
                            id="admin.templates.clone.summaryStatements"
                            defaultMessage="Statements:"
                        />
                    </Text>{' '}
                    <Text strong>
                        {mode === 'clone'
                            ? sourceTemplates.length
                            : importedRows.length}
                    </Text>
                </div>
                {mode === 'clone' ? (
                    <div>
                        <Text type="secondary">
                            <FormattedMessage
                                id="admin.templates.clone.summaryMods"
                                defaultMessage="Modifications:"
                            />
                        </Text>{' '}
                        <Text strong>{modificationCount}</Text>
                    </div>
                ) : (
                    <div>
                        <Text type="secondary">
                            <FormattedMessage
                                id="admin.templates.upload.summaryInactive"
                                defaultMessage="Inactive statements:"
                            />
                        </Text>{' '}
                        <Text strong>
                            {
                                importedRows.filter(
                                    (r) => r.status === 'inactive',
                                ).length
                            }
                        </Text>
                    </div>
                )}
            </Space>
        </div>
    );

    return (
        <Modal
            title={
                <span>
                    <CopyOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage
                        id="admin.templates.clone.title"
                        defaultMessage="Create New Template"
                    />
                </span>
            }
            open={open}
            onCancel={handleClose}
            width={
                currentStep === 1
                    ? mode === 'clone'
                        ? '95vw'
                        : 1100
                    : 640
            }
            footer={
                <Space>
                    {currentStep > 0 && (
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() =>
                                setCurrentStep((s) => s - 1)
                            }
                        >
                            <FormattedMessage
                                id="common.back"
                                defaultMessage="Back"
                            />
                        </Button>
                    )}
                    {currentStep < 2 && (
                        <Button
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            disabled={
                                currentStep === 0
                                    ? !canProceedStep0
                                    : !canProceedStep1
                            }
                            onClick={() =>
                                setCurrentStep((s) => s + 1)
                            }
                        >
                            <FormattedMessage
                                id="common.next"
                                defaultMessage="Next"
                            />
                        </Button>
                    )}
                    {currentStep === 2 && (
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={isCloning || isImporting}
                            onClick={
                                mode === 'clone'
                                    ? handleClone
                                    : handleCreateFromUpload
                            }
                        >
                            <FormattedMessage
                                id="admin.templates.clone.create"
                                defaultMessage="Create Template"
                            />
                        </Button>
                    )}
                </Space>
            }
        >
            <Steps
                current={currentStep}
                size="small"
                style={{ marginBottom: 24 }}
                items={[
                    {
                        title: intl.formatMessage({ id: 'admin.templates.clone.step1', defaultMessage: 'Setup' }),
                    },
                    {
                        title:
                            mode === 'clone'
                                ? intl.formatMessage({ id: 'admin.templates.clone.step2', defaultMessage: 'Edit Statements' })
                                : intl.formatMessage({ id: 'admin.templates.upload.step2', defaultMessage: 'Review Statements' }),
                    },
                    {
                        title: intl.formatMessage({ id: 'admin.templates.clone.step3', defaultMessage: 'Confirm' }),
                    },
                ]}
            />

            {currentStep === 0 && renderStep0()}
            {currentStep === 1 &&
                (mode === 'clone' ? renderStep1() : renderUploadPreview())}
            {currentStep === 2 && renderStep2()}

            <style>{`
                .upload-full-width,
                .upload-full-width .ant-upload {
                    display: block;
                    width: 100%;
                }
                .wizard-inactive-row {
                    background: #fff1f0 !important;
                }
                .wizard-inactive-row td {
                    text-decoration: line-through !important;
                    color: #cf1322 !important;
                    opacity: 0.65;
                }
                .wizard-inactive-row td .ant-tag,
                .wizard-inactive-row td .ant-switch,
                .wizard-inactive-row td .ant-select {
                    text-decoration: none !important;
                    opacity: 1;
                }
            `}</style>
        </Modal>
    );
};
